import {AddSchema} from "mobx-graphlink";
import {GetValues, GetValues_ForSchema} from "js-vextensions";
import {TriggerSet} from "../@TriggerSet.js";
import {Phase} from "../../@Shared/Phase.js";
import {EffectPointer} from "../../@Shared/EffectPointer.js";

export class EngineConfig_Visualization {
	enabled: boolean;

	startForceEnable_triggerSet: TriggerSet;
	/** In minutes; if set (>= 0), overrides the base forceEnabled_expireAfter value for the "start force-enabled" trigger. (null = use base value) */
	startForceEnable_expireAfterOverride: number|n;
	stopForceEnable_triggerSet: TriggerSet;
	stopForceEnabled_silently: boolean;
	toggleForceEnable_triggerSet: TriggerSet;
	/** In minutes; if set (>= 0), overrides the base forceEnabled_expireAfter value for the "toggle force-enabled" trigger (when it force-enables). (null = use base value) */
	toggleForceEnable_expireAfterOverride: number|n;
	nextPromptPool_triggerSet: TriggerSet;
	increaseTargetGridGen_triggerSet: TriggerSet;
	decreaseTargetGridGen_triggerSet: TriggerSet;
	increaseMaxGridGenDistance_triggerSet: TriggerSet;
	decreaseMaxGridGenDistance_triggerSet: TriggerSet;
	increaseInterval_triggerSet: TriggerSet;
	decreaseInterval_triggerSet: TriggerSet;
	increaseRevolutionDurations_triggerSet: TriggerSet;
	decreaseRevolutionDurations_triggerSet: TriggerSet;

	promptingBaseEnabledUnless_microSnoozeActive: boolean;
	promptingBaseEnabledUnless_mediaCompPlaying: boolean;
	promptingBaseEnabledUnless_inPhases: Phase[];

	forceEnabled_expireAfter: number; // in minutes
	forceEnabled_expireSilently: boolean;
	/** In seconds; default interval used by pools. (in case they don't specify a promptInterval_override that's != 0)
	Note: If using multiple pools, all pools should override this (in which case, this field is just to let user specify the "effective global prompt-interval", ie. averaged across all enabled, weight-applied pools) */
	promptInterval: number;
	promptInterval_step: number; // in seconds; step-size for interval-adjustment increases/decreases (from hotkeys/triggers)

	promptPools: PromptPool[];
}
AddSchema("EngineConfig_Visualization", {
	properties: {
		enabled: {type: "boolean"},

		startForceEnable_triggerSet: {$ref: "TriggerSet"},
		startForceEnable_expireAfterOverride: {type: ["null", "number"]},
		stopForceEnable_triggerSet: {$ref: "TriggerSet"},
		stopForceEnabled_silently: {type: "boolean"},
		toggleForceEnable_triggerSet: {$ref: "TriggerSet"},
		toggleForceEnable_expireAfterOverride: {type: ["null", "number"]},
		increaseInterval_triggerSet: {$ref: "TriggerSet"},
		decreaseInterval_triggerSet: {$ref: "TriggerSet"},
		increaseRevolutionDurations_triggerSet: {$ref: "TriggerSet"},
		decreaseRevolutionDurations_triggerSet: {$ref: "TriggerSet"},
		nextPromptPool_triggerSet: {$ref: "TriggerSet"},
		increaseTargetGridGen_triggerSet: {$ref: "TriggerSet"},
		decreaseTargetGridGen_triggerSet: {$ref: "TriggerSet"},
		increaseMaxGridGenDistance_triggerSet: {$ref: "TriggerSet"},
		decreaseMaxGridGenDistance_triggerSet: {$ref: "TriggerSet"},

		promptingBaseEnabledUnless_microSnoozeActive: {type: "boolean"},
		promptingBaseEnabledUnless_mediaCompPlaying: {type: "boolean"},
		promptingBaseEnabledUnless_inPhases: {type: "array", items: {$ref: "Phase"}},

		forceEnabled_expireAfter: {type: "number"},
		forceEnabled_expireSilently: {type: "boolean"},
		promptInterval: {type: "number"},
		promptInterval_step: {type: "number"},

		promptPools: {type: "array", items: {$ref: "PromptPool"}},
	},
});

export class PromptPool {
	constructor(data?: Partial<PromptPool>) {
		this.recencyBoosts = {};
		Object.assign(this, data);
	}

	name = "";
	enabled = true;
	weight = 100; // relative weight, compared to other pools
	lockIn_min = 1;
	lockIn_max = 1;
	lockIn_forceSwitchAfter = false;

	type = VisualizationPromptPoolType.custom;
	promptInterval_override: number|n = null; // in seconds; if set, overrides the global promptInterval for this pool
	promptVolume = .05;
	subpromptInterval: number|n; // in seconds
	subpromptVolumes = [.05] as number[]; // volume for each subprompt tick; if list is shorter than tick count, the last value is reused

	// partially-shared (if custom or brysbaert)
	ttsPrefix = "";
	ttsSuffix_forLastSubprompt = "";

	// custom
	distToNextTarget_max? = 10;
	distToNextTarget_min? = 1;
	equalizeSelectionWeightAmong? = VisPromptWeightGroup.room;
	blockTargetRepeatsForXPrompts? = 1;
	blockRoomRepeatsForXPrompts? = 0;
	customPoolStr? = "";

	// custom sounds
	cs_effect?: EffectPointer|n;

	// brysbaert
	concreteness_min? = 4.5;
	percentKnown_min? = .95;
	exclusionTags? = [] as string[];
	mergeDuplicates? = false;

	// grid scenes
	/** How many "generations away" from the target-generation the given scene can be (in either direction)
	 * for it to be included in the active pool. (0 = only scenes at exactly the target generation) */
	maxGridGenDistance = 0;
	/** Which generation the pool is centered on, relative to the latest scene for each A-Z grid-cell (0 = latest).
	 * Eg. 2 = target the scene two generations older than the latest.
	 * Included generation-window = [targetGridGen - maxGridGenDistance, targetGridGen + maxGridGenDistance]. */
	targetGridGen = 0;
	/** Pick-weight multipliers for gridScenes pools, keyed by signed "generation-offset" from the target
	 * (candidate.genDistance - targetGridGen; 0 = target, negative = newer, positive = older).
	 * A scene whose offset exactly matches a key gets its pick-weight multiplied by that value. */
	recencyBoosts: {[key: string]: number};
	/** If true, don't allow a cell to be repeated until all other cells have been used. (ie. "round-robin" selection) */
	avoidRepeatsTillAllCellsUsed? = true;
	/** After this many prompts (within this pool), increase the shared targetGridGen_liveAdjustment by 1.
	 * (works regardless of whether avoidRepeatsTillAllCellsUsed is enabled; -1/null = disabled)
	 * Eg. 26 = one bump per full A-Z cell sweep; 52 = two prompts per cell before the bump. */
	bumpTargetGenAfterXPrompts?: number|n;
	/** After this many straight minutes in "dreaming", reset the shared targetGridGen_liveAdjustment to 0.
	 * (-1/null = disabled; 0 = reset immediately on entering dreaming) */
	resetTargetGenAfterXMinsOfDreaming?: number|n;

	// TTS options (currently only applies for "custom" pool-type)
	ttsVoiceType? = TTSVoiceType.androidTTS;
	elevenLabsVoiceId? = ""; // only used when ttsVoiceType is "elevenLabs"
	ttsConvertDigitsToWords? = false; // convert "1" to "One", etc., for better TTS pronunciation
}
AddSchema("PromptPool", {
	properties: {
		name: {type: "string"},
		enabled: {type: "boolean"},
		weight: {type: "number"},
		lockIn_min: {type: "number"},
		lockIn_max: {type: "number"},
		lockIn_forceSwitchAfter: {type: "boolean"},

		type: {$ref: "VisualizationPromptPoolType"},
		promptInterval_override: {type: ["null", "number"]},
		promptVolume: {type: "number"},
		subpromptInterval: {type: ["null", "number"]},
		subpromptVolumes: {type: "array", items: {type: "number"}},

		ttsPrefix: {type: "string"},
		ttsSuffix_forLastSubprompt: {type: "string"},

		distToNextTarget_max: {type: "number"},
		distToNextTarget_min: {type: "number"},
		equalizeSelectionWeightAmong: {$ref: "VisPromptWeightGroup"},
		blockTargetRepeatsForXPrompts: {type: "number"},
		blockRoomRepeatsForXPrompts: {type: "number"},
		customPoolStr: {type: "string"},

		cs_effect: {$ref: "EffectPointer"},

		concreteness_min: {type: "number"},
		percentKnown_min: {type: "number"},
		exclusionTags: {type: "array", items: {type: "string"}},
		mergeDuplicates: {type: "boolean"},

		maxGridGenDistance: {type: "number"},
		targetGridGen: {type: "number"},
		recencyBoosts: {patternProperties: {"-?[0-9]+": {type: "number"}}},
		avoidRepeatsTillAllCellsUsed: {type: "boolean"},
		bumpTargetGenAfterXPrompts: {type: "number"},
		resetTargetGenAfterXMinsOfDreaming: {type: "number"},

		ttsVoiceType: {$ref: "TTSVoiceType"},
		elevenLabsVoiceId: {type: "string"},
		ttsConvertDigitsToWords: {type: "boolean"},
	},
});

export enum VisualizationPromptPoolType {
	custom = "custom",
	customSounds = "customSounds",
	brysbaert = "brysbaert",
	gridScenes = "gridScenes",
}
AddSchema("VisualizationPromptPoolType", {enum: GetValues(VisualizationPromptPoolType)});

export enum VisPromptWeightGroup {
	target = "target",
	room = "room",
}
AddSchema("VisPromptWeightGroup", {enum: GetValues(VisPromptWeightGroup)});

export enum TTSVoiceType {
	androidTTS = "androidTTS",
	elevenLabs = "elevenLabs",
}
AddSchema("TTSVoiceType", {enum: GetValues(TTSVoiceType)});